import { ArcData, Budget, BudgetPie, BudgetSchedule, BudgetSection, BudgetSummary, CashFlowItem, pieKeys, pieOpacities, pieLabels, LineItem, revenueSliceColors, costSliceColors } from "../../../globals/store/budget.data";
import { getSet, getNestedList, sortByPropOfNumber, jsonCopyObject } from "../../../globals/helpers/helpers";
import { fastIsEqual } from 'fast-is-equal';

import { LineItemTemplate } from "../../../globals/store/accounts.data";
import { updateBudgetInPlace, updateBudgetSectionInPlace } from "globals/helpers/budget.helper";

export const applyItemTemplateToLineItemInPlace = (budgetCurrency: string, section: BudgetSection, lineItemId: string, itemTemplate: LineItemTemplate, isCosts: boolean = true): void => {
  const lineItem = section.lineItems.find(i => i.id === lineItemId)
  const { conversionRate, currency } = section.settings
  const divider =  !currency || currency === budgetCurrency ? 1 : conversionRate || 1
  lineItem.item = itemTemplate.name
  lineItem.unitCost = (isCosts ? itemTemplate.unitCost : itemTemplate.unitRate) / divider
  if (isCosts) lineItem.itemRate = itemTemplate.unitRate
  lineItem.itemId = itemTemplate.id
}

export const linkedSectionHasChanges = (ignoreSchedule: boolean, linkSourceSection: BudgetSection, linkedCache: BudgetSection, propsToIgnore: string[] = []) => {
  if (!linkSourceSection || !linkedCache) return true // deleted

  propsToIgnore = propsToIgnore || []
  if (ignoreSchedule) propsToIgnore.push('schedule') // right now the only time a schedule matters 

  // console.log({ linkSourceSection, linkedCache })

  const one = jsonCopyObject(linkSourceSection) // need to do this because of deleting and because of differences in data after passing throught his function
  const two = jsonCopyObject(linkedCache)
  
  propsToIgnore.forEach(p => {
    delete one[p]
    delete two[p]
  })
  const hasChanges = !fastIsEqual(one,two)
  if (hasChanges) {
    // console.log({ hasChanges, one, two, diff: getObjectDiff(one,two) })
    window['one'] = one
    window['two'] = two
  }
  return hasChanges
}

/* export const applyMarkup = (bs: BudgetSection, markupMult: number, oldMarkupMult: number = 1) => {
  const newOldMarkupRatio = markupMult / oldMarkupMult
  console.log({ newOldMarkupRatio })
  bs.lineItems.filter(li => !li.itemId).forEach(li => li.unitCost = li.unitCost * newOldMarkupRatio)
}
*/ // markup can no longer be directly applied



export const convertLinkedCostSectionToRevenueSection = (section: BudgetSection, budgetCurrency: string): BudgetSection => { // for cost section previews
  const { conversionRate, currency } = section.settings
  const newSection = jsonCopyObject(section)
  const multiplier = currency && currency !== budgetCurrency ? (conversionRate || 1) : 1

  delete newSection.settings.currency // CANNOT LINK THE SECTION CURRENCY AND SET LINKED REVENUE CORRECTLY
  newSection.settings.conversionRate = 1 

  const { useMarkup, markupRate, useUnitRate, useCurrency } = section.settings
  newSection.lineItems.forEach(li => {
    if (useMarkup && li.markup) {
      li.unitCost = li.unitCost * multiplier * (1 + (markupRate || 0))
    } else if (useUnitRate) {
      li.unitCost = li.unitRate || (li.itemRate * multiplier) || 0 // per Rob's specifications, if unit rate is on and not filled out, it must be 0
    } else if (useCurrency && budgetCurrency !== currency) { // need to convert currency if use unit rate is not on but section currency is different.  Not sure why this correctly works but thank god.
      li.unitCost = li.unitCost * multiplier
    } else if (li.itemId) {
      li.unitCost = li.itemRate ? li.itemRate / multiplier : 0 // per Rob's specifications, if an item was set and it didn't have a unit rate, the revenue unit rate becomes 0
    }
  })
  updateBudgetSectionInPlace(newSection, budgetCurrency)
  return newSection
}

export const multiplyRevenueBudgetInPlace = (budget: Budget, multiplier: number = 1) => { // for the share in another currency part of budget 
  const { sections, revenueIds, revenuePaymentSchedule } = budget
  revenueIds.map(id => sections[id]).forEach(s => {
    s.lineItems.forEach(li => li.unitCost = (li.unitCost || 0) * multiplier)
  })
  revenuePaymentSchedule.items.forEach(i => i.amount = (i.amount  || 0) * multiplier)
  updateBudgetInPlace(budget)
}

const makePie = (sections: BudgetSection[], other: number = 0) => {
  const pie: BudgetPie = {
    quoted: 0,
    estimate: 0,
    actual: 0,
    total: other || 0,
    other,
  }
  sections.forEach(s => s.lineItems.filter(lineItem => lineItem.total).forEach(lineItem => {
    const amount = lineItem.total
    pie.total += amount
    switch (lineItem.status) {
      case 'Actual':
        pie.actual += amount
        break
      case 'Estimate':
        pie.estimate += amount
        break
      case 'Quote':
        pie.quoted += amount
        break
    }
  }))
  return pie
}


const makeArcs = (pie: BudgetPie, colorList: any): ArcData[] => {
  let position = 0
  return pieKeys.filter(k => pie[k]).sort((a,b) => pie[a] - pie[b]) // no values means no slice
    .map(k => {
      const percent = pie[k] / pie.total
      const startPercent = position
      const endPercent = position + percent
      position += percent
      return { color: colorList[k], startPercent, endPercent, label: `${pieLabels[k]} (${Math.round(percent * 100)}%)`}
    })
}

export const makeBudgetReport = (budget: Budget): BudgetSummary => {
  if (!budget) return null

  const revenueSubtotal = budget.summaryTaxInclusive ? budget.totalRevenue : budget.totalRevenue - budget.revenueTax
  const totalCosts = budget.summaryTaxInclusive ? budget.totalCosts : budget.totalCosts - budget.costsTax
  const totalRevenue = revenueSubtotal + (!budget.otherRevenues?.length ? 0 : budget.otherRevenues.reduce((total, c) => total + (c.amount || 0), 0))

  // console.log({ totalRevenue,budget, revenueSubtotal })

  const profit = totalRevenue - totalCosts
  const profitMargin = profit / totalRevenue
  
  const costSections = budget.costIds.map(id => budget.sections[id])
  const revenueSections = budget.revenueIds.map(id => budget.sections[id])

  const revenuePie = makePie(revenueSections, budget.otherRevenues[0].amount)
  const costsPie = makePie(costSections)

  const revenueScheduleItemIds = getSet(budget.revenuePaymentSchedule.items, 'id')

  

  const allScheduleItems = [...getNestedList(costSections.map(s => s.schedule).filter(s => s), 'items'), ...budget.revenuePaymentSchedule.items].sort(sortByPropOfNumber('date'))
  // console.log({ allScheduleItems })

  let balance = 0
  const cashFlow: CashFlowItem[] = allScheduleItems.map(({ date, description, amount, id, status, costSectionId }) => {
    const rev = revenueScheduleItemIds.has(id)
    balance += amount * (rev ? 1 : -1)
    const cashFlowItem: CashFlowItem = {
      date, 
      description,
      received: rev ? amount : null,
      billed: !rev ? amount : null,
      status,
      balance,
      costSectionId: costSectionId || null
    }
    return cashFlowItem
  })

  const budgetSummary: BudgetSummary = { 
    revenueSubtotal,
    totalRevenue,
    totalCosts,
    profit,
    profitMargin,
    revenueArcs: makeArcs(revenuePie, revenueSliceColors),
    costsArcs: makeArcs(costsPie, costSliceColors),
    cashFlow,
  }
  return budgetSummary
}

function flattten(arg0: (string | number)[][][]) {
  throw new Error("Function not implemented.");
}
